import { useEffect, useMemo, useRef, useState } from "react";
import { Loader2 } from "lucide-react";

import {
  fetchAthleteGoals,
  fetchAthleteProfile,
  fetchAvailability,
  saveAuthoritativeTrainingGoal,
} from "@/api/athleteGoalsApi";
import { fetchTrainingIntake } from "@/api/trainingIntakeApi";
import { fetchCurrentCoachingDecision } from "@/api/coachingDecisionApi";
import { CoachingSectionCard } from "@/components/coaching/CoachingSectionCard";
import { Button } from "@/components/ui/button";
import { generateQuickTrainingPlan, loadQuickPlanPrefill, quickPlanLifecycleErrorMessage, resolveQuickPlanHorizonWeeks } from "@/lib/quickPlanUpdate";
import { ACTIVE_PLAN_CHANGED_EVENT } from "@/lib/activePlanSync";
import { fetchCurrentActiveTrainingPlan } from "@/api/trainingPlanApi";
import {
  WEEKDAY_OPTIONS,
  buildAthleteGoalPayload,
  dayMaxMinutesFromAvailability,
  categoriesForSport,
  isEventCategory,
  sportConfig,
  targetDateFromHorizonMonths,
  GOAL_HORIZON_MONTH_OPTIONS,
  TRAINING_GOAL_SPORTS,
  START_PREFERENCE_OPTIONS,
  QUICK_PLAN_DURATION_OPTIONS,
  ISO_WEEKDAY_NAMES,
  resolveDaySessionCap,
} from "@/lib/trainingGoalOptions";

const inputCls = "app-control text-sm";

const FormSection = ({ title, children }) => (
  <div className="app-card-elevated p-4 sm:p-5 space-y-4">
    <h3 className="card-label">{title}</h3>
    {children}
  </div>
);

const selectedWeekdaysFromAvailability = (availability) => {
  const days = availability?.weekly_pattern?.days || [];
  return days
    .map((day) => Number(day.weekday))
    .filter((day) => day >= 1 && day <= 7);
};

export const TrainingGoalSetup = ({
  onSaved,
  onPlanGenerated,
  loadGoals = fetchAthleteGoals,
  loadProfile = fetchAthleteProfile,
  loadAvailability = fetchAvailability,
  loadIntake = fetchTrainingIntake,
  loadCoachingDecision = fetchCurrentCoachingDecision,
  saveGoal = saveAuthoritativeTrainingGoal,
  generatePlan = generateQuickTrainingPlan,
  loadActivePlan = fetchCurrentActiveTrainingPlan,
}) => {
  const [sportId, setSportId] = useState("cycling");
  const [goalCategory, setGoalCategory] = useState("ride_longer_without_fatigue");
  const [originalText, setOriginalText] = useState("");
  const [targetDate, setTargetDate] = useState("");
  const [horizonMode, setHorizonMode] = useState("unset");
  const [horizonMonths, setHorizonMonths] = useState("8");
  const [noEventDate, setNoEventDate] = useState(false);
  const [distanceKm, setDistanceKm] = useState("");
  const [ftpWatts, setFtpWatts] = useState("");
  const [weekdays, setWeekdays] = useState([]);
  const [startPreference, setStartPreference] = useState("START_TOMORROW");
  const [customStartDate, setCustomStartDate] = useState("");
  const [planHorizonWeeks, setPlanHorizonWeeks] = useState(4);
  const [preferredLongSessionDay, setPreferredLongSessionDay] = useState("");
  const [activePlanConflictChoice, setActivePlanConflictChoice] = useState("");
  const [hasLivePlan, setHasLivePlan] = useState(false);
  const [experienceLevel, setExperienceLevel] = useState("intermediate");
  const [longestSessionMinutes, setLongestSessionMinutes] = useState("90");
  const [currentGoal, setCurrentGoal] = useState(null);
  const [existingAvailability, setExistingAvailability] = useState(null);
  const [dayMaxMinutes, setDayMaxMinutes] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null);
  const onSavedRef = useRef(onSaved);
  onSavedRef.current = onSaved;

  const categories = useMemo(() => categoriesForSport(sportId), [sportId]);
  const showEventDate = isEventCategory(goalCategory);
  const showFtp = sportId === "cycling" && goalCategory === "improve_ftp";
  const showDistance = [
    "ride_longer_without_fatigue",
    "prepare_for_audax",
    "complete_target_distance",
    "increase_distance_safely",
    "improve_endurance",
  ].includes(goalCategory);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const [goals, profile, availability, planPrefill, activePlan] = await Promise.all([
          loadGoals(),
          loadProfile(),
          loadAvailability(),
          loadQuickPlanPrefill({ loadGoals, loadIntake }),
          loadActivePlan().catch(() => null),
        ]);
        if (cancelled) return;
        const primary = (goals?.items || []).find((item) => item.is_primary)
          || (goals?.items || [])[0]
          || null;
        setCurrentGoal(primary);
        if (typeof onSavedRef.current === "function" && primary) {
          onSavedRef.current({ goal: primary, profile });
        }
        if (primary) {
          const sports = primary.sports || [];
          const inferredSport = primary.sport
            || (sports.includes("swimming") && sports.includes("running")
              ? "triathlon"
              : sports.includes("running") && sports.includes("cycling")
                ? "duathlon"
                : sports[0] || "cycling");
          setSportId(inferredSport);
          setGoalCategory(primary.goal_category || categoriesForSport(inferredSport)[0]?.value);
          setOriginalText(primary.original_text || "");
          if (primary.target_date) {
            setTargetDate(primary.target_date);
            setHorizonMode("custom");
          } else {
            setTargetDate("");
            setHorizonMode("unset");
          }
          if (primary.target?.metric_type === "ftp") {
            setFtpWatts(String(primary.target.value || ""));
          }
          if (
            ["target_distance", "running_distance", "swimming_distance"]
              .includes(primary.target?.metric_type)
          ) {
            setDistanceKm(String(primary.target.value || ""));
          }
        }
        if (profile?.primary_sport && !primary?.sport) {
          setSportId(profile.primary_sport);
        }
        setWeekdays(selectedWeekdaysFromAvailability(availability));
        setExistingAvailability(availability || null);
        setDayMaxMinutes(dayMaxMinutesFromAvailability(availability));
        const longest = availability?.general_constraints?.longest_session_minutes
          || availability?.longest_session_minutes;
        if (longest) setLongestSessionMinutes(String(longest));
        setStartPreference(planPrefill.startPreference || "START_TOMORROW");
        setCustomStartDate(planPrefill.customStartDate || "");
        setPreferredLongSessionDay(
          planPrefill.preferredLongSessionDay
          || availability?.general_constraints?.preferred_long_session_day
          || "",
        );
        const livePlan = Boolean(activePlan?.block_id);
        setHasLivePlan(livePlan);
        setActivePlanConflictChoice(
          livePlan ? (planPrefill.activePlanConflictChoice || "") : "",
        );
        if (Number.isFinite(planPrefill.planHorizonWeeks)) {
          setPlanHorizonWeeks(planPrefill.planHorizonWeeks);
        }
        if (profile?.experience_level) {
          setExperienceLevel(profile.experience_level);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err?.goalError?.message || err?.message || "Could not load your training goal.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [loadActivePlan, loadAvailability, loadGoals, loadIntake, loadProfile]);

  useEffect(() => {
    const syncPlanHorizonWeeks = async () => {
      try {
        const activePlan = await loadActivePlan();
        setHasLivePlan(Boolean(activePlan?.block_id));
        setPlanHorizonWeeks(resolveQuickPlanHorizonWeeks(activePlan));
      } catch (_err) {
        // Keep the current selector value when the active plan cannot be read.
      }
    };
    window.addEventListener(ACTIVE_PLAN_CHANGED_EVENT, syncPlanHorizonWeeks);
    return () => window.removeEventListener(ACTIVE_PLAN_CHANGED_EVENT, syncPlanHorizonWeeks);
  }, [loadActivePlan]);

  useEffect(() => {
    if (!categories.some((item) => item.value === goalCategory)) {
      setGoalCategory(categories[0]?.value || "ride_longer_without_fatigue");
    }
  }, [categories, goalCategory]);

  const toggleWeekday = (value) => {
    setWeekdays((current) => {
      if (current.includes(value)) {
        return current.filter((day) => day !== value);
      }
      setDayMaxMinutes((caps) => ({
        ...caps,
        [value]: caps[value]
          || resolveDaySessionCap({
            weekday: value,
            dayMaxMinutes: caps,
            longestSessionMinutes,
          }),
      }));
      return [...current, value].sort((a, b) => a - b);
    });
  };

  const updateDayMinutes = (weekday, rawValue) => {
    const parsed = Number.parseInt(rawValue, 10);
    setDayMaxMinutes((caps) => ({
      ...caps,
      [weekday]: Number.isFinite(parsed) ? parsed : "",
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);
    setStatus(null);
    setSaving(true);
    try {
      if (weekdays.length === 0) {
        throw new Error("Choose at least one training day.");
      }
      const missingMinutes = weekdays.filter((day) => {
        const minutes = Number(dayMaxMinutes[day]);
        return !Number.isInteger(minutes) || minutes < 5 || minutes > 600;
      });
      if (missingMinutes.length > 0) {
        throw new Error("Enter training minutes (5–600) for each selected day.");
      }
      let resolvedDate = targetDate;
      if (!showEventDate && horizonMode === "months") {
        resolvedDate = targetDateFromHorizonMonths(Number(horizonMonths));
      } else if (!showEventDate && horizonMode === "unset") {
        resolvedDate = "";
      }
      const goalPayload = buildAthleteGoalPayload({
        sportId,
        goalCategory,
        originalText,
        targetDate: resolvedDate,
        noEventDate,
        distanceKm,
        ftpWatts,
        existingGoal: currentGoal,
      });
      const availabilityInputs = {
        sportId,
        weekdays,
        timezone: existingAvailability?.schedule_timezone || "UTC",
        longestSessionMinutes,
        dayMaxMinutes,
        preferredLongSessionDay,
      };
      const result = await saveGoal({
        goalPayload,
        existingGoal: currentGoal,
        primarySport: sportConfig(sportId).id === "triathlon" || sportConfig(sportId).id === "duathlon"
          ? sportId
          : sportConfig(sportId).id,
        availabilityInputs,
        existingAvailability,
        experienceLevel,
      });
      setCurrentGoal(result?.goal || null);
      const savedAvailability = result?.availability;
      if (savedAvailability?.revision) {
        setExistingAvailability(savedAvailability);
        setDayMaxMinutes(dayMaxMinutesFromAvailability(savedAvailability));
      }
      if (typeof onSaved === "function") {
        onSaved(result);
      }
      const planResult = await generatePlan({
        horizonWeeks: planHorizonWeeks,
        startPreference,
        customStartDate,
        activePlanConflictChoice: hasLivePlan ? (activePlanConflictChoice || null) : null,
        preferredLongSessionDay,
        loadCoachingDecision,
      });
      if (planResult?.completed) {
        const weekCount = planResult?.activation?.active_plan?.weeks?.length
          || planResult?.draft?.draft?.horizon_weeks
          || planHorizonWeeks;
        setPlanHorizonWeeks(weekCount);
        setStatus(`Your training plan was updated (${weekCount}-week plan active).`);
      } else {
        setStatus("Training plan draft ready. Confirm or replace your active plan below.");
      }
      if (typeof onPlanGenerated === "function") {
        onPlanGenerated(planResult);
      }
    } catch (err) {
      if (err?.planConflict) {
        setError(
          "Your current plan overlaps this start date. Choose whether to replace it or finish it first.",
        );
      } else {
        setError(quickPlanLifecycleErrorMessage(err));
      }
    } finally {
      setSaving(false);
    }
  };

  const goalForm = (
        <form onSubmit={handleSubmit} className="space-y-5" data-testid="training-goal-form">
          <FormSection title="Sport">
          <label className="block">
            <span className="card-label">Main sport</span>
            <select
              className={`${inputCls} mt-2`}
              value={sportId}
              onChange={(event) => setSportId(event.target.value)}
              data-testid="training-goal-sport"
            >
              {TRAINING_GOAL_SPORTS.map((sport) => (
                <option key={sport.id} value={sport.id}>{sport.label}</option>
              ))}
            </select>
          </label>
          </FormSection>

          <FormSection title="Goal">
          <label className="block">
            <span className="card-label">Primary goal</span>
            <select
              className={`${inputCls} mt-2`}
              value={goalCategory}
              onChange={(event) => setGoalCategory(event.target.value)}
              data-testid="training-goal-category"
            >
              {categories.map((item) => (
                <option key={item.value} value={item.value}>{item.label}</option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="card-label">Describe it</span>
            <textarea
              className={`${inputCls} mt-2`}
              rows={3}
              value={originalText}
              onChange={(event) => setOriginalText(event.target.value)}
              placeholder="Example: Ride 100 km comfortably without fading"
              data-testid="training-goal-text"
              required
            />
          </label>
          </FormSection>

          <FormSection title="Target">

          {currentGoal && !currentGoal.target_date && !showEventDate && (
            <p className="text-sm text-dark-text-secondary" data-testid="training-goal-set-date-prompt">
              Set a target date if you want Endurava to plan several training blocks toward a deadline. This is not filled in from phrases like “in 4 months”.
            </p>
          )}

          {showEventDate && (
            <div className="space-y-2">
              <label className="block">
                <span className="card-label">
                  Target event date (optional)
                </span>
                <input
                  type="date"
                  className={`${inputCls} mt-2`}
                  value={targetDate}
                  onChange={(event) => setTargetDate(event.target.value)}
                  disabled={noEventDate}
                  data-testid="training-goal-date"
                />
              </label>
              <label className="flex items-center gap-2 text-sm text-dark-text-secondary">
                <input
                  type="checkbox"
                  checked={noEventDate}
                  onChange={(event) => setNoEventDate(event.target.checked)}
                  data-testid="training-goal-no-date"
                />
                I don&apos;t have an event date
              </label>
            </div>
          )}

          {!showEventDate && (
            <div className="space-y-2">
              <label className="block">
                <span className="card-label">
                  Goal deadline (optional)
                </span>
                <select
                  className={`${inputCls} mt-2`}
                  value={horizonMode === "months" ? `months-${horizonMonths}` : horizonMode}
                  onChange={(event) => {
                    const value = event.target.value;
                    if (value === "unset" || value === "custom") {
                      setHorizonMode(value);
                      return;
                    }
                    if (value.startsWith("months-")) {
                      setHorizonMode("months");
                      setHorizonMonths(value.replace("months-", ""));
                    }
                  }}
                  data-testid="training-goal-horizon"
                >
                  <option value="unset">Not set yet</option>
                  {GOAL_HORIZON_MONTH_OPTIONS.map((months) => (
                    <option key={months} value={`months-${months}`}>
                      {months} months
                    </option>
                  ))}
                  <option value="custom">Custom date</option>
                </select>
              </label>
              {horizonMode === "custom" && (
                <label className="block">
                  <span className="card-label">
                    Target date
                  </span>
                  <input
                    type="date"
                    className={`${inputCls} mt-2`}
                    value={targetDate}
                    onChange={(event) => setTargetDate(event.target.value)}
                    data-testid="training-goal-custom-date"
                  />
                </label>
              )}
              <p className="text-xs text-dark-text-muted">
                This is the goal deadline, not the length of the next training block. Endurava stores a date, not “8 months” as text.
              </p>
            </div>
          )}

          {showDistance && (
            <label className="block">
              <span className="card-label">
                {sportId === "swimming" ? "Target distance (metres, optional)" : "Target distance (km, optional)"}
              </span>
              <input
                type="number"
                min="0"
                step="0.1"
                className={`${inputCls} mt-2`}
                value={distanceKm}
                onChange={(event) => setDistanceKm(event.target.value)}
                data-testid="training-goal-distance"
              />
            </label>
          )}

          {showFtp && (
            <label className="block">
              <span className="card-label">
                Target FTP (watts, optional)
              </span>
              <input
                type="number"
                min="0"
                className={`${inputCls} mt-2`}
                value={ftpWatts}
                onChange={(event) => setFtpWatts(event.target.value)}
                data-testid="training-goal-ftp"
              />
            </label>
          )}
          </FormSection>

          <FormSection title="Schedule">
          <fieldset>
            <legend className="card-label">Training days</legend>
            <div className="mt-2 flex flex-wrap gap-2">
              {WEEKDAY_OPTIONS.map((day) => (
                <label
                  key={day.value}
                  className={`app-badge cursor-pointer transition-colors ${
                    weekdays.includes(day.value)
                      ? "border-brand-primary text-brand-primary"
                      : "border-[var(--border-primary)] text-dark-text-secondary"
                  }`}
                >
                  <input
                    type="checkbox"
                    className="sr-only"
                    checked={weekdays.includes(day.value)}
                    onChange={() => toggleWeekday(day.value)}
                    data-testid={`training-goal-day-${day.value}`}
                  />
                  {day.label}
                </label>
              ))}
            </div>
          </fieldset>

          {weekdays.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {weekdays.map((weekday) => {
                const label = WEEKDAY_OPTIONS.find((day) => day.value === weekday)?.label || weekday;
                return (
                  <label key={weekday} className="block">
                    <span className="card-label">{label} minutes</span>
                    <input
                      type="number"
                      min="5"
                      max="600"
                      className={`${inputCls} mt-2`}
                      value={dayMaxMinutes[weekday] ?? ""}
                      onChange={(event) => updateDayMinutes(weekday, event.target.value)}
                      data-testid={`training-goal-day-minutes-${weekday}`}
                      required
                    />
                  </label>
                );
              })}
            </div>
          )}

          <label className="block">
            <span className="card-label">Preferred long session day (optional)</span>
            <select
              className={`${inputCls} mt-2`}
              value={preferredLongSessionDay}
              onChange={(event) => setPreferredLongSessionDay(event.target.value)}
              data-testid="training-goal-long-day"
            >
              <option value="">No preference</option>
              {weekdays.map((weekday) => (
                <option key={weekday} value={ISO_WEEKDAY_NAMES[weekday]}>
                  {WEEKDAY_OPTIONS.find((day) => day.value === weekday)?.label}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="card-label">Typical longest session (minutes)</span>
            <input
              type="number"
              min="15"
              max="720"
              className={`${inputCls} mt-2`}
              value={longestSessionMinutes}
              onChange={(event) => setLongestSessionMinutes(event.target.value)}
              data-testid="training-goal-longest-session"
            />
          </label>
          </FormSection>

          <FormSection title="Plan">
          <label className="block">
            <span className="card-label">Start preference</span>
            <select
              className={`${inputCls} mt-2`}
              value={startPreference}
              onChange={(event) => setStartPreference(event.target.value)}
              data-testid="quick-plan-start-preference"
            >
              {START_PREFERENCE_OPTIONS.map((option) => (
                <option key={option.id} value={option.id}>{option.label}</option>
              ))}
            </select>
          </label>

          {startPreference === "CUSTOM_DATE" && (
            <label className="block">
              <span className="card-label">Custom start date</span>
              <input
                type="date"
                className={`${inputCls} mt-2`}
                value={customStartDate}
                onChange={(event) => setCustomStartDate(event.target.value)}
                data-testid="quick-plan-custom-start-date"
              />
            </label>
          )}

          <label className="block">
            <span className="card-label">Plan duration</span>
            <select
              className={`${inputCls} mt-2`}
              value={planHorizonWeeks}
              onChange={(event) => setPlanHorizonWeeks(Number(event.target.value))}
              data-testid="quick-plan-duration-weeks"
            >
              {QUICK_PLAN_DURATION_OPTIONS.map((weeks) => (
                <option key={weeks} value={weeks}>
                  {weeks} week{weeks === 1 ? "" : "s"}
                </option>
              ))}
            </select>
          </label>

          {hasLivePlan && (
          <fieldset className="space-y-2" data-testid="quick-plan-current-plan-choices">
            <legend className="card-label">If your current plan overlaps this start</legend>
            <label className="flex items-start gap-2 text-sm">
              <input
                type="radio"
                name="active-plan-conflict"
                checked={activePlanConflictChoice === "REPLACE_CURRENT_PLAN"}
                onChange={() => setActivePlanConflictChoice("REPLACE_CURRENT_PLAN")}
                data-testid="quick-plan-replace-current"
              />
              Replace my current plan
            </label>
            <label className="flex items-start gap-2 text-sm">
              <input
                type="radio"
                name="active-plan-conflict"
                checked={activePlanConflictChoice === "FINISH_CURRENT_PLAN_FIRST"}
                onChange={() => setActivePlanConflictChoice("FINISH_CURRENT_PLAN_FIRST")}
                data-testid="quick-plan-finish-current"
              />
              Finish my current plan first
            </label>
          </fieldset>
          )}
          </FormSection>

          <div className="flex flex-wrap items-center gap-2">
          <Button type="submit" disabled={saving || !originalText.trim()} data-testid="training-goal-save" className="btn-primary-app">
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                Updating…
              </>
            ) : hasLivePlan ? (
              "Generate / Update Training Plan"
            ) : (
              "Create Training Plan"
            )}
          </Button>
          </div>
        </form>
  );

  return (
    <CoachingSectionCard
      title="Quick Plan Update"
      subtitle="Change your goal, schedule, or plan duration here. One-time profile setup is optional."
      headingId="training-goal-heading"
      testId="training-goal-setup"
    >
      <div id="training-goal" className="scroll-mt-24" />
      {loading ? (
        <p className="text-sm text-dark-text-secondary">Loading your plan controls…</p>
      ) : (
        goalForm
      )}

      {error && (
        <p role="alert" data-testid="training-goal-error" className="mt-4 text-sm text-analytics-amber">
          {error}
        </p>
      )}
      {status && (
        <p data-testid="training-goal-status" className="mt-4 text-sm text-dark-text-secondary">
          {status}
        </p>
      )}
    </CoachingSectionCard>
  );
};

export default TrainingGoalSetup;
